import React from 'react';
import { motion } from 'framer-motion';
import { X } from 'lucide-react';

interface EmotionGuideProps {
  isOpen: boolean;
  onClose: () => void;
}

const EmotionGuide: React.FC<EmotionGuideProps> = ({ isOpen, onClose }) => {
  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/50 flex items-start justify-center z-50 p-4 overflow-y-auto"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="bg-[#3A7D78] p-8 rounded-2xl text-white shadow-lg border border-white/20 max-w-4xl w-full my-8"
        onClick={e => e.stopPropagation()}
      >
        <div className="flex justify-between items-start mb-6">
          <h2 className="text-2xl font-bold">情绪/感受词汇说明书</h2>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors p-1"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        <div className="prose prose-invert max-w-none">
          <p className="text-white/80">本说明书旨在精确定义60个词汇。每个词汇的释义都侧重于其在特定维度下的心理体验，并与其他易混淆的词汇进行区分，为产品设计和智能解读提供统一、精准的依据。</p>

          <h3 className="text-xl font-semibold mt-8 mb-4">【任务维度】(15个词)</h3>
          <div className="space-y-6">
            <div>
              <h4 className="font-medium mb-2">1. 耗竭</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：因长时间或高强度的付出后，内在资源（如心力、精力）被消耗殆尽的感觉。</li>
                <li>关键区别：与"懈怠"不一样。"耗竭"是"我没能量了"；"懈怠"是"我有能量，但不想做"。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">2. 兴奋</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：对即将开始或正在进行中的任务，感到激动、充满热情和期待。</li>
                <li>关键区别：比"满意"更具前瞻性和过程性，唤醒度高于"投入"。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">3. 投入</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：全神贯注于当前任务，忘记时间流逝的沉浸感（心流/Flow状态）。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">4. 困惑</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：因缺乏信息、理解或方向，而感到迷惘、不知道下一步该做什么的状态。</li>
                <li>关键区别：与"两难"不同。"困惑"是"不知道有什么路可选"；"两难"是"知道有两条路，但无法选择"。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">5. 两难</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：在两个或多个明确的选项之间，因利弊冲突或代价巨大而无法做出决定的冲突和停滞感。</li>
                <li>关键区别：是决策过程中的核心冲突，比日常的"纠结"分量更重。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">6. 懈怠</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：主观意愿上的松弛、懒散，缺乏动力去开始或继续执行任务。</li>
                <li>关键区别：是动机的缺失或消散，而非能量的耗尽（耗竭）。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">7. 满意</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：对任务成果感到骄傲、欣赏和满足。是对“产出”的喜悦。</li>
                <li>关键区别：与"得心应手"不同。"满意"聚焦于结果；"得心应手"聚焦于过程。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">8. 忍耐</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：为了长远目标，主动承受和克制在任务过程中遇到的困难、乏味或痛苦。</li>
                <li>关键区别：强调"意志力"的持续作用，是一种带有目的性的坚持。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">9. 得心应手</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：在任务执行过程中，因技能纯熟而感到的流畅、自如。</li>
                <li>关键区别：是对过程的体验，而非对结果的评价（满意）。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">10. 沮丧</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：因任务受挫、失败或进展不顺而引起的失落。</li>
                <li>关键区别：比"失望"更侧重于"事情搞砸了"的无力感。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">11. 雀跃</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：因获得成功或突破性进展而感到的欣喜或狂喜。</li>
                <li>关键区别：比"兴奋"更强烈，比"满意"更外显。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">12. 茫然</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：完全失去了方向感，对任务的前景和路径感到一片空白。</li>
                <li>关键区别：比"困惑"更深、更严重，是"连问题都不知道在哪"的状态。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">13. 焦虑</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：面对有挑战的任务时，因担心自身能力不足、害怕失败或无法承担责任，而产生的持续性的、混杂着担忧、不安和紧张的内心状态。</li>
                <li>关键区别：它不同于"紧迫"。"紧迫"的压力源于时间；"焦虑"的压力源于对能力和结果的不自信。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">14. 松快</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：在完成一项紧张或有压力的任务后，身心从紧绷状态中解脱出来的舒畅和放松感。</li>
                <li>关键区别：是一种"事后"的释放感。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">15. 乏味</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：因任务内容重复、单调、缺乏挑战而缺乏兴致。</li>
                <li>关键区别：是针对任务"内容"本身的感受，而非自身能量（耗竭）或意愿（懈怠）。</li>
              </ul>
            </div>
          </div>

          <h3 className="text-xl font-semibold mt-8 mb-4">【人际维度】(22个词)</h3>
          <div className="space-y-6">
            <div>
              <h4 className="font-medium mb-2">1. 失望</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：对他人的言行或关系状态，感到期望落空的失落感。</li>
                <li>关键区别：指向"人"，而非指向"事"（沮丧）。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">2. 害羞</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：在社交场合，因害怕他人负面评价而感到的不自在、拘谨和退缩。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">3. 心虚</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：因自知理亏、犯错或能力不足，而害怕被发现的底气不足和不自然感。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">4. 心软</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：因同情或不忍心，而容易原谅或帮助他人的倾向。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">5. 孤单</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：因缺乏所期望的社会连接，而感到的不悦和痛苦。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">6. 羡慕</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：看到他人拥有自己所没有的优点或好处时，产生的向往之情。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">7. 自卑</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：在与他人比较中，感觉自己能力、价值或地位低下的一种慢性的、痛苦的自我评价。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">8. 信任</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：相信他人的品格、意图和能力，是一种理性的、对关系稳固性的判断和信念。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">9. 亲近</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：在情感上感觉与他人距离很近，充满喜爱、熟悉和归属感。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">10. 排斥</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：在情感或行为上，抗拒、疏远、不接纳他人。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">11. 鄙夷</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：从内心深处看不起、蔑视他人，认为对方在品格或价值上低于自己。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">12. 生气</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：因感觉被冒犯、不公对待或期望受阻而产生的愤怒情绪。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">13. 憋屈</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：感觉受到冤枉、不公或压制，但又无法或不敢申辩，导致愤怒和无力感在内心郁结。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">14. 优越</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：在与他人比较中，感觉自己能力、价值或地位更高。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">15. 惦记</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：因关心或思念，而时常想起某人。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">16. 感动</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：因他人的言行或某种情境，内心受到触动而产生的一种欣慰、同情、敬佩的复合情绪。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">17. 心碎</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：因遭遇重大的情感打击（如背叛、失恋、失去挚爱）而感到的极致的、毁灭性的痛苦。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">18. 迷恋</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：对某人产生强烈的、通常带有理想化色彩的痴迷和沉醉感。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">19. 心动</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：对某人或某事产生好感或爱慕的"第一瞬间"。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">20. 嫉妒</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：因担心自己珍视的关系、机会、资源被威胁或夺走，而产生的愤怒、不安和怀疑。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">21. 亏欠</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：感觉自己辜负了他人，或未能报答他人的恩情，而产生的一种对不起对方的、类似情感债务的感觉。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">22. 温暖</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：感受到他人的善意、关怀和接纳时，内心产生的一种舒适、被滋养的感受。</li>
              </ul>
            </div>
          </div>

          <h3 className="text-xl font-semibold mt-8 mb-4">【时间维度】(11个词)</h3>
          <div className="space-y-6">
            <div>
              <h4 className="font-medium mb-2">1. 充实</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：感觉时间被有价值、有意义的活动填满了，是一种对“时间质量”的积极体验。</li>
                <li>关键区别：是“空虚”的完美反义词。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">2. 无聊</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：因缺乏有意义的刺激，而感觉时间过得缓慢、内容空洞。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">3. 着急</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：因希望事情快点发生或马上符合预期，而产生的焦急、不耐烦的心态。</li>
                <li>关键区别：是“从容”的直接反义词。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">4. 从容</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：在时间压力下，或想要达到的目标前，保持镇定、不慌不忙的内在心态和掌控力。</li>
                <li>关键区别：是主动的沉着，而非被动的“悠闲”。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">5. 怀念</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：对过去的人、事、物进行美好的、带有思念色彩的回忆。</li>
                <li>关键区别：与“遗憾”不同。“怀念”是对“有过”的美好追忆；“遗憾”是对“未有”的惋惜。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">6. 遗憾</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：对过去因各种原因未能实现的事，感到惋惜和不圆满。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">7. 期待</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：对未来将要发生的美好事情，怀着愉快的心情等待。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">8. 悠闲</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：拥有充裕的、可自由支配的时间，是一种放松、不匆忙的状态。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">9. 紧迫</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：意识到某个重要的机会窗口正在关闭或关键节点即将到来，所产生的压力感。其内核是“机不可失，时不再来”的害怕失去。</li>
                <li>它与“着急”有本质区别。“着急”是一种主观上“嫌慢”的情绪，可以针对任何小事。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">10. 煎熬</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：在痛苦或困境中，感觉时间被无限拉长，每一分每一秒都充满折磨。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">11. 空虚</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：感觉时间流逝得毫无意义，内心没有寄托，是一种深层的精神空洞感。</li>
              </ul>
            </div>
          </div>

          <h3 className="text-xl font-semibold mt-8 mb-4">【空间维度】(12个词)</h3>
          <div className="space-y-6">
            <div>
              <h4 className="font-medium mb-2">1. 压抑</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：感觉心理空间受到外部环境或气氛的挤压，无法自由舒展。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">2. 舒适</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：在所处环境中，身心都感到轻松、愉快、没有不适感。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">3. 自在</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：能够不受外界拘束，随心所欲地展现真实自我的自由感。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">4. 拘谨</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：在特定环境中，因不自在或在意他人看法而感到言行拘束、放不开。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">5. 窒息</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：感觉心理空间被完全剥夺，如同无法呼吸般的极致压迫感。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">6. 浮躁</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：内心无法安定下来，有一种不踏实、轻飘飘、静不下来的焦躁感。是“踏实”的完美反义词。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">7. 踏实</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：内心安稳、有根基，有一种脚踩大地的稳固和可靠感。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">8. 束缚</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：感觉被规则、他人或环境牢牢捆绑，无法自由行动或表达。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">9. 舒展</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：身心从收缩、拘谨的状态中伸展开来，感到放松和通透。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">10. 轻盈</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：一种没有负担，也没有明确目标的正面体验，身体和心灵都变得轻快、灵敏。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">11. 沉静</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：内心深沉而宁静，不受外界纷扰，是一种安稳、平和的状态。</li>
              </ul>
            </div>
            <div>
              <h4 className="font-medium mb-2">12. 暴露</h4>
              <ul className="list-disc pl-5 space-y-2 text-white/80">
                <li>核心释义：感觉个人边界被侵犯，内心世界或隐私被看穿，失去保护而感到脆弱和无处可藏。</li>
              </ul>
            </div>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default EmotionGuide;